import { auth } from "../config/firebase";

const CLOUD_FUNCTION_URL = import.meta.env.VITE_GOOGLE_DRIVE_CONFIG_URL ||
  'https://getgoogledriveconfig-p3aid3mwoq-uc.a.run.app';

const SCOPES = ['https://www.googleapis.com/auth/drive.readonly'];

let tokenClient;
let accessToken = null;
let CLIENT_ID = null;
let API_KEY = null;

/**
 * Fetch Google Drive API credentials from Cloud Function
 */
async function fetchGoogleDriveConfig() {
  try {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const idToken = await user.getIdToken();

    const response = await fetch(CLOUD_FUNCTION_URL, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch Google Drive configuration");
    }

    const config = await response.json();
    CLIENT_ID = config.clientId;
    API_KEY = config.apiKey;

    return config;
  } catch (error) {
    console.error("Error fetching Google Drive config:", error);
    throw error;
  }
}


/**
 * Load Google Picker and initialize with credentials from Cloud Function
 */
export async function loadGooglePicker(onPick) {
  try {
    // Fetch credentials from Cloud Function first
    await fetchGoogleDriveConfig();

    // Load the picker API
    window.gapi.load('picker', () => {
      initTokenClient(onPick);
    });
  } catch (error) {
    console.error('Error loading Google Picker:', error);
    throw error;
  }
}

function initTokenClient(onPick) {
  if (!CLIENT_ID) {
    console.error('Client ID not loaded');
    return;
  }

  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPES.join(' '),
    callback: (response) => {
      accessToken = response.access_token;
      createPicker(onPick);
    },
  });

  tokenClient.requestAccessToken({ prompt: 'consent' });
}

function createPicker(onPick) {
  if (!API_KEY) {
    console.error('API Key not loaded');
    return;
  }

  const picker = new window.google.picker.PickerBuilder()
    .addView(window.google.picker.ViewId.DOCS)
    .setOAuthToken(accessToken)
    .setDeveloperKey(API_KEY)
    .setCallback((data) => {
      if (data.action === window.google.picker.Action.PICKED) {
        // Pass both docs and the accessToken so we can download the file content
        onPick(data.docs, accessToken);
      }
    })
    .build();

  picker.setVisible(true);
}

/**
 * Download a file from Google Drive using the provided access token
 * Handles both binary files (direct download) and Google Docs (export)
 * @param {string} fileId - ID of the file to download
 * @param {string} accessToken - OAuth access token
 * @returns {Promise<{data: string, mimeType: string}>} Base64 data and mime type
 */
export async function downloadDriveFile(fileId, accessToken) {
  try {
    // 1. Get file metadata to check mimeType
    const metadataResponse = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=mimeType,name`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!metadataResponse.ok) throw new Error("Failed to fetch file metadata");
    const metadata = await metadataResponse.json();

    let url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
    let exportMimeType = metadata.mimeType;

    // 2. Handle Google Docs formats (Must be exported)
    if (metadata.mimeType === 'application/vnd.google-apps.document') {
      exportMimeType = 'application/pdf'; // or 'text/plain'
      url = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${exportMimeType}`;
    } else if (metadata.mimeType === 'application/vnd.google-apps.spreadsheet') {
      exportMimeType = 'application/pdf'; // or 'text/csv'
      url = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${exportMimeType}`;
    } else if (metadata.mimeType === 'application/vnd.google-apps.presentation') {
      exportMimeType = 'application/pdf';
      url = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${exportMimeType}`;
    }

    // 3. Download (or Export)
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download/export file: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Convert to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve({
          data: base64String,
          mimeType: exportMimeType // Use the exported mime type (e.g. application/pdf)
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error downloading Drive file:', error);
    throw error;
  }
}
